import {
  cancelBooking,
  cancelBookingEnc,
  editBooking,
  editBookingEnc,
  getAvailableTimeSlots,
  getAvailableTimeSlotsDetail,
} from "api/services/Form";
import Button from "components/Button";
import DatePicker from "components/DatePicker";
import Popup from "components/Popup";
import StepIndicator from "components/StepIndicator";
import TimePicker from "components/TimePicker";
import { useSecSession } from "context/SecSessionContext";
import {
  AvailableTimeslot,
  Booking,
  CancelBookingRes,
  EditBookingRes,
  GetAvailableTimeSlotsRes,
} from "entities/Form";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "react-responsive";
import { MAX_WIDTH_MOBILE } from "utils/constant";
import {
  areAllTimeSlotsDisabledOrEmpty,
  constructSecurePayload,
  getApiLangParam,
  getLocalizedValue,
  isSameMonthYear,
  resetDateToMidnight,
  tzStartDay,
} from "utils/helper";
import { ReactComponent as IconReminder } from "../../../assets/reminder.svg";
import { ReactComponent as IconWarning } from "../../../assets/warning.svg";
import { POPUP_STATUS } from "../../../entities";
import styles from "./BookingDetail.module.scss";

interface BookingProps {
  otpSid?: string;
  booking: Booking;
  notes?: string;
  enquiryPhoneNo?: string;
  isEdit: boolean;
  isCancel: boolean;
  onBackToHomePage: () => void;
  onSuccessCancelBooking: (res: CancelBookingRes) => void;
  onSuccessEditBooking: (res: EditBookingRes) => void;
}

const BookingDetail: React.FC<BookingProps> = ({
  otpSid,
  booking,
  notes,
  enquiryPhoneNo,
  isEdit,
  isCancel,
  onBackToHomePage,
  onSuccessCancelBooking,
  onSuccessEditBooking,
}) => {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: MAX_WIDTH_MOBILE });
  const { secSession } = useSecSession();
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelAppointmentPopup, setCancelAppointmentPopup] = useState(false);
  const [canceling, setCanceling] = useState(false);

  const datePickerRef = useRef<ReactDatePicker>(null);
  const dateTimeBlockRef = useRef<HTMLDivElement>(null);

  const [fromDt, setFromDt] = useState<string>();
  const [toDt, setToDt] = useState<string>();
  const [minDate, setMinDate] = useState<Date>();
  const [maxDate, setMaxDate] = useState<Date>();
  const [availableTimeSlots, setAvailableTimeSlots] =
    useState<GetAvailableTimeSlotsRes | null>();
  const [availableTimeSlotsDetail, setAvailableTimeSlotsDetail] =
    useState<AvailableTimeslot[]>();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTimeSlot, setSelectedTimeSlot] =
    useState<GetAvailableTimeSlotsRes | null>();
  const [selectedTimeSlotsDetail, setSelectedTimeSlotsDetail] =
    useState<AvailableTimeslot[]>();

  const submit = async () => {
    if (!fromDt || !toDt) return;
    setSubmitting(true);

    const onEditBookingError = () => {
      setSubmitting(false);
    };

    secSession
      ? editBookingEnc(
          await constructSecurePayload(
            {
              lang: getApiLangParam(i18n.language),
              bookingNo: booking.bookingNo,
              hid: booking.hid,
              fromDt,
              toDt,
              otpSid,
            },
            secSession.pk,
          ),
          onSuccessEditBooking,
          onEditBookingError,
        )
      : editBooking(
          {
            bookingNo: booking.bookingNo,
            hid: booking.hid,
            fromDt,
            toDt,
            otpSid,
          },
          onSuccessEditBooking,
          onEditBookingError,
        );
  };

  const onCancelBooking = async () => {
    setCanceling(true);

    const onCancelBookingError = () => {
      setCanceling(false);
    };

    secSession
      ? cancelBookingEnc(
          await constructSecurePayload(
            {
              lang: getApiLangParam(i18n.language),
              hid: booking.hid,
              otpSid,
            },
            secSession.pk,
          ),
          onSuccessCancelBooking,
          onCancelBookingError,
        )
      : cancelBooking(
          { hid: booking.hid, otpSid },
          onSuccessCancelBooking,
          onCancelBookingError,
        );
  };

  const noTimeSlot =
    availableTimeSlots &&
    areAllTimeSlotsDisabledOrEmpty(availableTimeSlots.timeSlotDictionary) &&
    isSameMonthYear(
      new Date(availableTimeSlots.minDate),
      new Date(availableTimeSlots.maxDate),
    );

  const getTimeSlots = (date?: Date, updateSelctedTimeSlot: boolean = true) => {
    const fromDt = moment(date).startOf("month").toDate();
    const toDt = moment(date).endOf("month").toDate();

    return new Promise<GetAvailableTimeSlotsRes>((resolve, reject) => {
      getAvailableTimeSlots(
        {
          serviceItemHid: booking.serviceItem.hid,
          careProviderHid: booking.careProvider.value,
          fromDt,
          toDt,
        },
        (res) => {
          setAvailableTimeSlots(res);
          if (updateSelctedTimeSlot) setSelectedTimeSlot(res);
          setMinDate(moment(res.minDate).toDate());
          setMaxDate(moment(res.maxDate).toDate());
          resolve(res);
        },
        (err) => {
          setAvailableTimeSlots(null);
          if (updateSelctedTimeSlot) setSelectedTimeSlot(null);
          setMinDate(undefined);
          setMaxDate(undefined);
          reject(err);
        },
      );
    });
  };

  const onChangeTimeSlot = (
    fromDt: string,
    toDt: string,
    careProviderHid?: string,
  ) => {
    const selectedDate = new Date(new Date(fromDt).setHours(0, 0, 0, 0));

    const displayingTimeSlotsDateKey = availableTimeSlots?.timeSlotDictionary
      ? Object.keys(availableTimeSlots.timeSlotDictionary)?.[0]
      : undefined;

    const displayingTimeSlotsDate = resetDateToMidnight(
      displayingTimeSlotsDateKey,
    );

    if (
      displayingTimeSlotsDate &&
      isSameMonthYear(displayingTimeSlotsDate, selectedDate)
    )
      setSelectedTimeSlot(availableTimeSlots);
    else {
      const availableTimeSlotsDateKey = selectedTimeSlot?.timeSlotDictionary
        ? Object.keys(selectedTimeSlot.timeSlotDictionary)?.[0]
        : undefined;

      const availableTimeSlotsDate = resetDateToMidnight(
        availableTimeSlotsDateKey,
      );

      if (
        !availableTimeSlotsDate ||
        !isSameMonthYear(availableTimeSlotsDate, selectedDate)
      )
        getTimeSlots(selectedDate);
    }

    setSelectedTimeSlotsDetail(availableTimeSlotsDetail);
    // onSelectTime(
    //   `${formatDate(fromDt!)} ${getLocalizedValue({
    //     name:
    //       availableTimeSlotsDetail?.find(
    //         (timeSlot) => timeSlot.fromDt === fromDt
    //       )?.label || "",
    //     nameTc:
    //       availableTimeSlotsDetail?.find(
    //         (timeSlot) => timeSlot.fromDt === fromDt
    //       )?.labelTc || "",
    //     nameSc:
    //       availableTimeSlotsDetail?.find(
    //         (timeSlot) => timeSlot.fromDt === fromDt
    //       )?.labelSc || "",
    //   })}`
    // );
    setFromDt(fromDt);
    setToDt(toDt);
  };

  useEffect(() => {
    const availableTimeSlotsDetailDate = resetDateToMidnight(
      availableTimeSlotsDetail?.[0]?.fromDt,
    );

    if (
      selectedDate &&
      availableTimeSlotsDetailDate &&
      moment(selectedDate).isSame(availableTimeSlotsDetailDate, "day")
    )
      return;

    if (selectedDate) {
      setFromDt(undefined);
      setToDt(undefined);
      getAvailableTimeSlotsDetail(
        {
          serviceItemHid: booking.serviceItem.hid,
          careProviderHid: booking.careProvider.value,
          fromDt: moment(selectedDate).toDate(),
        },
        (res) => setAvailableTimeSlotsDetail(res.availableTimeslots),
        () => setAvailableTimeSlotsDetail([]),
      );
      if (!availableTimeSlots) getTimeSlots(selectedDate);
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!selectedDate && fromDt) setSelectedDate(resetDateToMidnight(fromDt));
  }, [fromDt]);

  useEffect(() => {
    if (!editing) return;
    if (selectedTimeSlotsDetail)
      setAvailableTimeSlotsDetail(selectedTimeSlotsDetail);
    setAvailableTimeSlots(selectedTimeSlot);

    const formDate = resetDateToMidnight(fromDt);

    const displayDate =
      formDate || moment(booking.serviceItem.minDate).toDate();

    setSelectedDate(formDate);

    const datePickerInstance = (datePickerRef.current as any)?.calendar
      ?.instanceRef;
    if (datePickerInstance && displayDate) {
      !formDate && selectedTimeSlot && selectedTimeSlot.timeSlotDictionary
        ? datePickerInstance.changeMonthYear(
            Object.keys(selectedTimeSlot.timeSlotDictionary)[0],
          )
        : datePickerInstance.changeMonthYear(displayDate);
    }

    const isSelectTimeslot = !!Object.keys(
      selectedTimeSlot?.timeSlotDictionary || {},
    ).length;

    if (!isSelectTimeslot)
      getTimeSlots(displayDate)?.then((slots) => {
        if (slots && areAllTimeSlotsDisabledOrEmpty(slots.timeSlotDictionary)) {
          const slotMaxDate = new Date(slots.maxDate);
          if (
            datePickerInstance &&
            ((slotMaxDate.getFullYear() === displayDate.getFullYear() &&
              slotMaxDate.getMonth() > displayDate.getMonth()) ||
              slotMaxDate.getFullYear() > displayDate.getFullYear())
          ) {
            const nextMonthDate = moment(displayDate)
              .add(1, "month")
              .startOf("month")
              .toDate();
            datePickerInstance.changeMonthYear(nextMonthDate);
            getTimeSlots(nextMonthDate);
          }
        }
      });
  }, [booking, editing]);

  useEffect(() => {
    if (booking) {
      setMinDate(moment(booking.serviceItem.minDate).toDate());
      setMaxDate(moment(booking.serviceItem.maxDate).toDate());
    }
  }, [booking]);

  return (
    <>
      <div
        className={`${styles.container} ${isMobile ? styles.mobile : ""} ${
          submitting ? styles.submitting : ""
        }`}
      >
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <StepIndicator value={3} total={3} />
            <span className={styles.title}>
              {t(editing ? "editOrCancelAppointment" : "myAppointment")}
            </span>
          </div>
          <Button
            type="secondary"
            onClick={onBackToHomePage}
            className={styles.button}
          >
            {t("backToHomePage")}
          </Button>
        </div>
        <div className={styles.content}>
          <div
            key={booking.bookingNo}
            className={`${styles.block} ${editing ? styles.editing : ""}`}
          >
            <div className={styles.titleContainer}>
              <span className={styles.title}>{booking.bookingNo}</span>
              <div className={styles.actionButtons}>
                {!editing && isEdit && (
                  <Button
                    type="tertiary"
                    className={styles.button}
                    onClick={() => {
                      setEditing(true);
                      setTimeout(() => {
                        dateTimeBlockRef.current?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        });
                      }, 300);
                    }}
                  >
                    {t("rescheduleAppointment")}
                  </Button>
                )}
                {isCancel && (
                  <Button
                    type="tertiary"
                    className={styles.button}
                    onClick={() => setCancelAppointmentPopup(true)}
                    disabled={submitting}
                  >
                    {t("cancelAppointment")}
                  </Button>
                )}
              </div>
            </div>
            {(!isEdit || !isCancel) && (
              <div className={styles.warningContainer}>
                <div className={styles.warningIcon}>
                  <IconWarning />
                </div>
                <span>
                  {!isEdit && !isCancel
                    ? t("appointmentCantBeCancelledOrEdited")
                    : !isEdit
                      ? t("appointmentCantBeEdited")
                      : t("appointmentCantBeCancelled")}
                  {i18n.language === "en" && " "}
                  {t("inquiriesContact", { phoneNo: enquiryPhoneNo })}
                </span>
              </div>
            )}
            <div className={styles.header}>{t("outpatientService")}</div>
            <div className={styles.detail}>
              {getLocalizedValue({
                nameTc: booking.department.nameTc,
                nameSc: booking.department.nameSc,
                name: booking.department.name,
              })}
            </div>
            <div className={styles.header}>{t("doctor")}</div>
            <div className={styles.detail}>{booking.careProvider.name}</div>
            <div className={styles.header}>{t("appointmentTimeSlot")}</div>
            <div className={styles.detail}>
              {getLocalizedValue({
                nameTc: booking.bookingTimeLabelTc,
                nameSc: booking.bookingTimeLabelSc,
                name: booking.bookingTimeLabel,
              })}
            </div>
          </div>
        </div>
        {editing && (
          <>
            <div className={styles.dateTimeBlock} ref={dateTimeBlockRef}>
              <div className={styles.dateTimeContainer}>
                <span className={styles.title}>
                  {t("editAppointmentTimeSlot")}
                </span>
                <div className={styles.legends}>
                  <div className={styles.legend}>
                    <div
                      className={`${styles.icon} ${styles.amAvaliable}`}
                    ></div>
                    <span>{t("amAvailable")}</span>
                  </div>
                  <div className={styles.legend}>
                    <div
                      className={`${styles.icon} ${styles.pmAvaliable}`}
                    ></div>
                    <span>{t("pmAvailable")}</span>
                  </div>
                  <div className={styles.legend}>
                    <div
                      className={`${styles.icon} ${styles.amPmAvaliable}`}
                    ></div>
                    <span>{t("amPmAvailable")}</span>
                  </div>
                  <div className={styles.legend}>
                    <div
                      className={`${styles.icon} ${styles.selectedDate}`}
                    ></div>
                    <span>{t("selectedDate")}</span>
                  </div>
                </div>
                <div className={styles.dateTimePicker}>
                  <div className={styles.datePickerWithReminder}>
                    <div className={styles.datePickerNoTimeSlotContainer}>
                      {noTimeSlot ? (
                        <div className={styles.noTimeSlot}>
                          {t("noTimeSlot")}
                        </div>
                      ) : (
                        <div
                          className={styles.datePickerContainer}
                          style={
                            (selectedDate && !availableTimeSlotsDetail) ||
                            submitting
                              ? { opacity: 0.3, pointerEvents: "none" }
                              : { opacity: 1 }
                          }
                        >
                          <div className={styles.datePicker}>
                            <DatePicker
                              datePickerRef={datePickerRef}
                              date={selectedDate}
                              onMonthChange={(date) => {
                                setAvailableTimeSlots(undefined);
                                getTimeSlots(date, false);
                              }}
                              onChange={(date) => {
                                setAvailableTimeSlotsDetail(undefined);
                                const timeSlotDate = Object.keys(
                                  availableTimeSlots?.timeSlotDictionary || {},
                                ).find((dt) =>
                                  moment(tzStartDay(dt)).isSame(date, "date"),
                                );
                                if (timeSlotDate) {
                                  setSelectedDate(
                                    moment(timeSlotDate).toDate(),
                                  );
                                }
                              }}
                              filterDate={
                                !minDate || !maxDate ? () => false : undefined
                              }
                              minDate={minDate}
                              maxDate={maxDate}
                              excludedDates={
                                availableTimeSlots?.timeSlotDictionary
                                  ? Object.keys(
                                      availableTimeSlots.timeSlotDictionary,
                                    ).length > 0
                                    ? Object.keys(
                                        availableTimeSlots.timeSlotDictionary,
                                      )
                                        .filter(
                                          (key) =>
                                            availableTimeSlots
                                              .timeSlotDictionary![key]
                                              .isDisabled ||
                                            (availableTimeSlots
                                              .timeSlotDictionary![key]
                                              .subGroups[0].roomSlot === 0 &&
                                              availableTimeSlots
                                                .timeSlotDictionary![key]
                                                .subGroups[1].roomSlot === 0),
                                        )
                                        .map((date) => new Date(date))
                                    : []
                                  : []
                              }
                              dayClassName={(date) => {
                                const timeSlotDate = Object.keys(
                                  availableTimeSlots?.timeSlotDictionary || {},
                                ).find((dt) =>
                                  moment(tzStartDay(dt)).isSame(date, "date"),
                                );
                                if (
                                  availableTimeSlots?.timeSlotDictionary &&
                                  timeSlotDate
                                ) {
                                  const day =
                                    availableTimeSlots.timeSlotDictionary[
                                      timeSlotDate
                                    ];

                                  const amGroup = day.subGroups.find(
                                    (group) => group.name === "AM",
                                  );
                                  const pmGroup = day.subGroups.find(
                                    (group) => group.name === "PM",
                                  );
                                  const amAvailable =
                                    amGroup &&
                                    (amGroup.fulfillSlot || amGroup.roomSlot);
                                  const pmAvailable =
                                    pmGroup &&
                                    (pmGroup.fulfillSlot || pmGroup.roomSlot);

                                  if (amAvailable && pmAvailable) return "full";
                                  else if (amAvailable) return "am";
                                  else if (pmAvailable) return "pm";
                                  else return "react-datepicker__day--disabled";
                                }
                                return "react-datepicker__day--disabled";
                              }}
                              isLoading={availableTimeSlots === undefined}
                              disable={availableTimeSlots === null}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    {notes && (
                      <div className={styles.reminder}>
                        <IconReminder />
                        <span>{notes}</span>
                      </div>
                    )}
                  </div>
                  {!noTimeSlot && (
                    <div className={styles.timePicker}>
                      <TimePicker
                        selectedDate={selectedDate}
                        selectedTime={fromDt}
                        availableTimeslots={availableTimeSlotsDetail}
                        onSelect={onChangeTimeSlot}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.buttons}>
              <Button
                type="primary"
                onClick={submit}
                loading={submitting}
                disabled={!fromDt || !toDt}
              >
                <span>{t("confirm")}</span>
              </Button>
            </div>
          </>
        )}
      </div>
      <Popup
        isOpen={cancelAppointmentPopup}
        title={t("cancelAppointment")}
        content={t("cancelAppointmentConfirmation")}
        status={POPUP_STATUS.WARNING}
        closeTitle={t("cancel")}
        onClose={() => setCancelAppointmentPopup(false)}
        confirmTitle={t("confirmAction")}
        onConfirm={() => onCancelBooking()}
        closeBtnProps={{ disabled: canceling }}
        confirmBtnProps={{
          loading: canceling,
          loadingDisplayType: "iconOnly",
        }}
        {...(canceling ? { slideModalProps: { onClose: () => {} } } : {})}
      />
    </>
  );
};

export default BookingDetail;
